import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Dialog, DialogContent, Button, Typography, Stack, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import confetti from 'canvas-confetti';
import { Link } from 'react-router-dom';
import { hapticHeavy } from '../lib/haptics';

/**
 * FIRST POST CELEBRATION
 *
 * Listens for the global `wipe-first-post` window event (fired by
 * SubmitWithTimeButton when a user creates their first-ever post) and
 * shows a fullscreen confetti dialog. Once-only via localStorage flag —
 * even if the event fires again, we no-op.
 *
 * The dialog frames the experience: "your post is alive — go invite
 * friends or share it." This is the most retention-critical moment in
 * the funnel because it's where new users first feel the dopamine hit
 * of the time mechanic.
 */

const STORAGE_KEY = 'wipe_first_post_celebrated';

function fireBigConfetti() {
  const duration = 2400;
  const end = Date.now() + duration;
  // Confetti uses raw CSS — Nokia accents (amber/lime/red) instead of the
  // old neon purple+cyan palette so the celebration matches the rest of
  // the app. Theme tokens don't work here; canvas-confetti expects literals.
  const colors = ['#0b0f0b', '#7a5400', '#2f5a1c', '#a02018', '#cdd8c5'];

  const frame = () => {
    confetti({
      particleCount: 6,
      angle: 60,
      spread: 70,
      origin: { x: 0, y: 0.6 },
      colors,
      zIndex: 99999,
    });
    confetti({
      particleCount: 6,
      angle: 120,
      spread: 70,
      origin: { x: 1, y: 0.6 },
      colors,
      zIndex: 99999,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  };
  frame();
}

function FirstPostCelebration() {
  const [open, setOpen] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    const handler = () => {
      try {
        if (localStorage.getItem(STORAGE_KEY) === '1') return;
      } catch { /* ignore */ }
      if (firedRef.current) return;
      firedRef.current = true;
      try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
      setOpen(true);
      hapticHeavy();
      // Slight delay so the dialog has time to mount
      setTimeout(fireBigConfetti, 200);
    };
    window.addEventListener('wipe-first-post', handler);
    return () => window.removeEventListener('wipe-first-post', handler);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 0,
          background: (t) => t.palette.background.paper,
          color: 'text.primary',
          border: '2px solid currentColor',
          boxShadow: '6px 6px 0 0 currentColor',
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 3, sm: 4 }, position: 'relative' }}>
        <IconButton
          size="small"
          onClick={() => setOpen(false)}
          sx={{ position: 'absolute', top: 12, right: 12, color: 'text.disabled' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography
            sx={{
              fontFamily: '"VT323", monospace',
              letterSpacing: 4,
              fontSize: { xs: '2rem', sm: '2.6rem' },
              color: 'text.primary',
              lineHeight: 1.1,
              mb: 1,
            }}
          >
            ━━ YOUR FIRST POST ━━
          </Typography>
          <Typography
            sx={{
              fontFamily: '"VT323", monospace',
              fontSize: { xs: '1.4rem', sm: '1.7rem' },
              letterSpacing: 2,
              color: 'text.primary',
              fontWeight: 700,
            }}
          >
            IS LIVE
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center', mb: 1, lineHeight: 1.5 }}>
          You just spent some of your time wallet to bring a thought into the
          world. It's now public, ticking down toward expiry.
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', mb: 3, fontStyle: 'italic' }}>
          Keep it alive with votes. Or let it die. Either way it counts.
        </Typography>

        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            border: '2px dashed',
            borderColor: 'divider',
            bgcolor: 'transparent',
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700, letterSpacing: 1, display: 'block' }}>
            ⚡ NEXT MOVES
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
            Invite a friend (+7 days) · Fire a Spark · Vote on someone else
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            component={Link}
            to="/invite"
            onClick={() => setOpen(false)}
            variant="contained"
            fullWidth
            startIcon={<CardGiftcardIcon />}
            sx={{
              bgcolor: 'text.primary',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': { bgcolor: 'text.primary' },
            }}
          >
            Invite friends
          </Button>
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            fullWidth
            startIcon={<IosShareIcon />}
            sx={{
              color: 'white',
              borderColor: 'text.primary',
              textTransform: 'none',
              '&:hover': { borderColor: 'white', bgcolor: 'action.hover' },
            }}
          >
            Keep browsing
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default FirstPostCelebration;
